import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Pencil, Save, X, Flame, MapPin, Mail, Phone, Quote } from "lucide-react";

const DEFAULT_ABOUT_APP = `The Local Loot is a community cash hunt.

We hide real money — in real envelopes with real codes — somewhere in your city. Then we drop the clues, one at a time, right here in this app. First hunter to follow the clues, find the envelope, and enter the code gets to keep the cash.

No catch. No gimmicks. Just clues, cash, and community fun.

Want to help fund the next drop? Local businesses can sponsor a hunt and get their brand in front of every hunter who plays — your logo shows up on the drop page, in the winner post, and on our sponsors wall. Scroll through the packages on the "Sponsor a drop" page to see how it works.`;

const DEFAULT_ABOUT_MICKEY = `Hey, I'm Mickey Trivett — the guy behind The Local Loot.

I started this because I wanted to give something back to the towns that raised me. No strings. No email list grabs. Just cash hidden in real places, clues posted in real time, and real people finding real money.

Every drop I self-fund or match a local business's sponsorship. Every winner goes up on the wall. Every city gets a say in where we go next.

If you've got a business that wants to sponsor a drop, or you want to pitch your city for the next hunt, hit me up — I'm easy to find.

Thanks for playing. Stay ready. Pay attention.`;

const DEFAULT_ABOUT_CONTACT = `Got a question, a city to pitch, or a business that wants to sponsor? Here's how to reach me.

Email: hi@thelocalloot.com
Phone: (423) 555-0199
Instagram: @thelocalloot

I read everything. I answer most. The fastest way to the top of the inbox is a clear subject line — "sponsor", "city pitch", or "press".`;

const DEFAULT_MISSION = "Live Life. Do Good. Be Happy.";
const DEFAULT_MISSION_BY = "— Mick";
const PILLAR_COLORS = ["#76B82A", "#D7A62A", "#E53935"];

function EditableMission() {
  const { user } = useAuth();
  const [value, setValue] = useState(DEFAULT_MISSION);
  const [byline, setByline] = useState(DEFAULT_MISSION_BY);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftBy, setDraftBy] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, b] = await Promise.all([
          api.get("/content/about_mission").catch(() => ({ data: {} })),
          api.get("/content/about_mission_by").catch(() => ({ data: {} })),
        ]);
        setValue(m.data?.value || DEFAULT_MISSION);
        setByline(b.data?.value || DEFAULT_MISSION_BY);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const startEdit = () => {
    setDraft(value);
    setDraftBy(byline);
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        api.put("/content/about_mission", { value: draft.trim() || DEFAULT_MISSION }),
        api.put("/content/about_mission_by", { value: draftBy.trim() || DEFAULT_MISSION_BY }),
      ]);
      setValue(draft.trim() || DEFAULT_MISSION);
      setByline(draftBy.trim() || DEFAULT_MISSION_BY);
      setEditing(false);
      toast.success("Mission updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Split on period boundaries while keeping the period attached
  const phrases = (value.match(/[^.!?]+[.!?]?/g) || [value])
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <section
      className="loot-card-flat p-8 md:p-10 relative overflow-hidden"
      data-testid="about-mission"
      style={{
        background:
          "linear-gradient(135deg, rgba(118,184,42,0.08) 0%, rgba(215,166,42,0.06) 50%, rgba(229,57,53,0.06) 100%)",
        borderColor: "rgba(245,242,232,0.15)",
      }}
    >
      <Quote
        size={120}
        className="absolute -top-4 -left-4 opacity-[0.06] pointer-events-none"
        style={{ color: "#F5F2E8" }}
      />
      <div className="flex items-start justify-between gap-4 mb-4 relative">
        <div className="overline">The mission</div>
        {user?.role === "admin" && !editing && (
          <button
            onClick={startEdit}
            className="btn-ghost text-xs inline-flex items-center gap-1"
            data-testid="edit-mission"
          >
            <Pencil size={12} /> Edit
          </button>
        )}
      </div>

      {!loaded ? (
        <div className="text-[#F5F2E8]/40 font-mono text-sm">Loading…</div>
      ) : editing ? (
        <div className="relative">
          <label className="block font-mono text-[10px] uppercase tracking-wider text-[#F5F2E8]/50 mb-1">
            Mission line
          </label>
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="loot-input"
            data-testid="textarea-mission"
            placeholder="Live Life. Do Good. Be Happy."
            maxLength={160}
          />
          <label className="block font-mono text-[10px] uppercase tracking-wider text-[#F5F2E8]/50 mt-3 mb-1">
            Attribution
          </label>
          <input
            type="text"
            value={draftBy}
            onChange={(e) => setDraftBy(e.target.value)}
            className="loot-input"
            data-testid="textarea-mission-by"
            placeholder="— Mick"
            maxLength={60}
          />
          <div className="mt-3 flex gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="btn-loot text-xs inline-flex items-center gap-1"
              data-testid="save-mission"
            >
              <Save size={12} /> {saving ? "..." : "Save"}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="btn-ghost text-xs inline-flex items-center gap-1"
            >
              <X size={12} /> Cancel
            </button>
          </div>
          <p className="mt-2 font-mono text-[10px] text-[#F5F2E8]/40">
            Tip: each sentence (separated by a period) gets its own brand color.
          </p>
        </div>
      ) : (
        <div className="relative">
          <p
            className="font-display uppercase tracking-tight leading-[1.05] text-3xl sm:text-4xl md:text-5xl"
            data-testid="mission-line"
          >
            {phrases.map((p, i) => (
              <span
                key={i}
                style={{ color: PILLAR_COLORS[i % PILLAR_COLORS.length] }}
                className="inline-block mr-2"
              >
                {p}
              </span>
            ))}
          </p>
          <div
            className="mt-5 font-mono text-xs tracking-widest text-[#F5F2E8]/60 uppercase"
            data-testid="mission-by"
          >
            {byline}
          </div>
        </div>
      )}
    </section>
  );
}

function EditableSection({ contentKey, defaultValue, title, children }) {
  const { user } = useAuth();
  const [value, setValue] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/content/${contentKey}`);
      setValue(data.value || defaultValue);
    } catch {
      setValue(defaultValue);
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); }, [contentKey]);

  const startEdit = () => {
    setDraft(value);
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/content/${contentKey}`, { value: draft });
      setValue(draft);
      setEditing(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="loot-card-flat p-6 md:p-8" data-testid={`about-section-${contentKey}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>{title}</div>
        {user?.role === "admin" && !editing && (
          <button
            onClick={startEdit}
            className="btn-ghost text-xs inline-flex items-center gap-1"
            data-testid={`edit-${contentKey}`}
          >
            <Pencil size={12} /> Edit
          </button>
        )}
      </div>
      {children}
      {!loaded ? (
        <div className="text-[#F5F2E8]/40 font-mono text-sm">Loading…</div>
      ) : editing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={10}
            className="loot-input"
            data-testid={`textarea-${contentKey}`}
          />
          <div className="mt-3 flex gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="btn-loot text-xs inline-flex items-center gap-1"
              data-testid={`save-${contentKey}`}
            >
              <Save size={12} /> {saving ? "..." : "Save"}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="btn-ghost text-xs inline-flex items-center gap-1"
            >
              <X size={12} /> Cancel
            </button>
          </div>
          <p className="mt-2 font-mono text-[10px] text-[#F5F2E8]/40">
            Markdown-ish: blank lines become paragraphs.
          </p>
        </div>
      ) : (
        <div className="prose-like space-y-4 text-[#F5F2E8]/80 text-base leading-relaxed">
          {value.split(/\n\s*\n/).map((p, i) => (
            <p key={i} className="whitespace-pre-line">{p}</p>
          ))}
        </div>
      )}
    </section>
  );
}

export default function AboutPage() {
  return (
    <div className="py-8 space-y-8">
      <div>
        <div className="overline">The story behind the hunt</div>
        <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight mt-2">
          About <span style={{ color: "#76B82A" }}>The Local Loot</span>
        </h1>
      </div>

      <EditableMission />

      <EditableSection
        contentKey="about_app"
        defaultValue={DEFAULT_ABOUT_APP}
        title={
          <div>
            <div className="overline flex items-center gap-1">
              <Flame size={12} /> What is this?
            </div>
            <h2 className="font-display uppercase text-2xl md:text-3xl tracking-tight mt-2">
              The hunt, explained.
            </h2>
          </div>
        }
      />

      <EditableSection
        contentKey="about_mickey"
        defaultValue={DEFAULT_ABOUT_MICKEY}
        title={
          <div>
            <div className="overline flex items-center gap-1">
              <MapPin size={12} /> The person
            </div>
            <h2 className="font-display uppercase text-2xl md:text-3xl tracking-tight mt-2">
              About <span style={{ color: "#D7A62A" }}>Mickey Trivett</span>.
            </h2>
          </div>
        }
      >
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-16 h-16 border border-[#F5F2E8]/20 flex items-center justify-center font-display text-3xl"
            style={{ background: "#123A1A", color: "#D7A62A" }}
          >
            MT
          </div>
          <div>
            <div className="font-display uppercase text-xl leading-tight">Mickey Trivett</div>
            <div className="font-mono text-xs text-[#F5F2E8]/50 mt-1">Founder · The Local Loot</div>
          </div>
        </div>
      </EditableSection>

      <EditableSection
        contentKey="about_contact"
        defaultValue={DEFAULT_ABOUT_CONTACT}
        title={
          <div>
            <div className="overline flex items-center gap-1">
              <Phone size={12} /> Get in touch
            </div>
            <h2 className="font-display uppercase text-2xl md:text-3xl tracking-tight mt-2">
              Contact <span style={{ color: "#76B82A" }}>Mickey</span>.
            </h2>
          </div>
        }
      />

      <section
        className="loot-card-flat p-8 md:p-10 text-center"
        style={{
          background: "linear-gradient(135deg, rgba(118,184,42,0.08), rgba(15,15,15,1) 60%)",
          borderColor: "#76B82A",
        }}
      >
        <h3 className="font-display uppercase text-3xl md:text-4xl tracking-tight">
          Want to <span style={{ color: "#76B82A" }}>sponsor</span> a drop or{" "}
          <span style={{ color: "#D7A62A" }}>pitch</span> your city?
        </h3>
        <p className="mt-3 text-[#F5F2E8]/70 max-w-lg mx-auto">
          Fund a hunt, vote where we go next, or just come play. Every hunter makes
          the community stronger.
        </p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <a href="/sponsor-apply" className="btn-loot inline-flex items-center gap-2">
            <Mail size={14} /> Sponsor a drop
          </a>
          <a href="/vote" className="btn-ghost">Vote your city</a>
        </div>
      </section>
    </div>
  );
}
